import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    BookOpen, Clock, ArrowRight, Search, Filter,
    TrendingUp, Sparkles, ChevronRight, Calendar,
    Eye, Tag
} from 'lucide-react';
import SEO, { seoConfigs } from '../components/SEO';
import { blogAPI } from '../utils/api';

const BlogListPage = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const categories = [
        { id: 'all', label: 'All Posts', icon: BookOpen },
        { id: 'career-guidance', label: 'Career Guidance', icon: TrendingUp },
        { id: 'resume', label: 'Resume Tips', icon: Tag },
        { id: 'skills', label: 'Skills', icon: Sparkles },
        { id: 'interview', label: 'Interview', icon: Filter },
    ];

    useEffect(() => {
        fetchPosts();
    }, [page]);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const data = await blogAPI.getAllPosts(page, 12);
            setPosts(prev => page === 1 ? data.posts : [...prev, ...data.posts]);
            setHasMore(data.has_more);
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredPosts = posts.filter(post => {
        const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const featuredPost = filteredPosts[0];
    const regularPosts = filteredPosts.slice(1);

    return (
        <>
            <SEO {...seoConfigs.blog} />

            <div className="min-h-screen bg-[#030712] pt-20">
                {/* Hero Section */}
                <section className="relative py-20 px-4 overflow-hidden">
                    {/* Background effects */}
                    <div className="absolute inset-0">
                        <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px]" />
                        <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-[100px]" />
                    </div>

                    <div className="max-w-6xl mx-auto relative z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center mb-12"
                        >
                            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-6">
                                <BookOpen className="w-4 h-4 text-emerald-400" />
                                <span className="text-emerald-400 text-sm font-medium">Career Insights Blog</span>
                            </div>

                            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                                Expert Career{' '}
                                <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                                    Guidance
                                </span>
                            </h1>
                            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                                Actionable insights, tips, and strategies to accelerate your career growth
                            </p>
                        </motion.div>

                        {/* Search & Filter Bar */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="flex flex-col md:flex-row gap-4 max-w-4xl mx-auto"
                        >
                            {/* Search */}
                            <div className="flex-1 relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                <input
                                    type="text"
                                    placeholder="Search articles..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                                />
                            </div>

                            {/* Category Filters */}
                            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
                                {categories.map((cat) => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setSelectedCategory(cat.id)}
                                        className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${selectedCategory === cat.id
                                            ? 'bg-emerald-500 text-black'
                                            : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white border border-white/10'
                                            }`}
                                    >
                                        <cat.icon className="w-4 h-4" />
                                        <span className="hidden sm:inline">{cat.label}</span>
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Featured Post */}
                {featuredPost && (
                    <section className="px-4 pb-16">
                        <div className="max-w-6xl mx-auto">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                <Link
                                    to={`/blog/${featuredPost.slug}`}
                                    className="block group relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border border-white/10 hover:border-emerald-500/30 transition-all"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />

                                    <div className="relative p-8 md:p-12">
                                        <div className="flex items-center space-x-4 mb-6">
                                            <span className="flex items-center space-x-1 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/40 rounded-full">
                                                <Sparkles className="w-3 h-3 text-emerald-400" />
                                                <span className="text-xs font-semibold text-emerald-400 uppercase">Featured</span>
                                            </span>
                                            <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs text-slate-400 capitalize">
                                                {featuredPost.category?.replace('-', ' ')}
                                            </span>
                                        </div>

                                        <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 group-hover:text-emerald-400 transition-colors leading-tight">
                                            {featuredPost.title}
                                        </h2>

                                        <p className="text-slate-400 text-lg mb-6 line-clamp-2 max-w-3xl">
                                            {featuredPost.excerpt}
                                        </p>

                                        <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500">
                                            <span className="flex items-center space-x-2">
                                                <Calendar className="w-4 h-4" />
                                                <span>{new Date(featuredPost.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                            </span>
                                            <span className="flex items-center space-x-2">
                                                <Clock className="w-4 h-4" />
                                                <span>{featuredPost.read_time_minutes} min read</span>
                                            </span>
                                            <span className="flex items-center space-x-2">
                                                <Eye className="w-4 h-4" />
                                                <span>{featuredPost.views || 0} views</span>
                                            </span>
                                        </div>

                                        <div className="absolute bottom-8 right-8 md:bottom-12 md:right-12">
                                            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-emerald-500/30">
                                                <ArrowRight className="w-5 h-5 text-black" />
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        </div>
                    </section>
                )}

                {/* Posts Grid */}
                <section className="px-4 pb-24">
                    <div className="max-w-6xl mx-auto">
                        {loading && posts.length === 0 ? (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <div key={i} className="bg-white/5 rounded-2xl p-6 animate-pulse">
                                        <div className="h-4 bg-white/10 rounded w-1/4 mb-4" />
                                        <div className="h-6 bg-white/10 rounded w-3/4 mb-3" />
                                        <div className="h-4 bg-white/10 rounded w-full mb-2" />
                                        <div className="h-4 bg-white/10 rounded w-2/3" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {regularPosts.map((post, index) => (
                                        <motion.div
                                            key={post.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.1 + index * 0.05 }}
                                        >
                                            <Link
                                                to={`/blog/${post.slug}`}
                                                className="block h-full group bg-white/[0.02] hover:bg-white/[0.05] border border-white/10 hover:border-emerald-500/30 rounded-2xl p-6 transition-all"
                                            >
                                                <div className="flex items-center justify-between mb-4">
                                                    <span className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-xs font-medium text-emerald-400 capitalize">
                                                        {post.category?.replace('-', ' ') || 'Career'}
                                                    </span>
                                                    <span className="flex items-center space-x-1 text-xs text-slate-500">
                                                        <Clock className="w-3 h-3" />
                                                        <span>{post.read_time_minutes} min</span>
                                                    </span>
                                                </div>

                                                <h3 className="text-lg font-bold text-white mb-3 group-hover:text-emerald-400 transition-colors line-clamp-2 leading-snug">
                                                    {post.title}
                                                </h3>

                                                <p className="text-sm text-slate-400 mb-4 line-clamp-3">
                                                    {post.excerpt}
                                                </p>

                                                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                                                    <span className="text-xs text-slate-500">
                                                        {new Date(post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                                    </span>
                                                    <span className="flex items-center space-x-1 text-emerald-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
                                                        <span>Read More</span>
                                                        <ChevronRight className="w-4 h-4" />
                                                    </span>
                                                </div>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Load More */}
                                {hasMore && (
                                    <div className="text-center mt-12">
                                        <button
                                            onClick={() => setPage(prev => prev + 1)}
                                            disabled={loading}
                                            className="inline-flex items-center space-x-2 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/20 rounded-xl font-semibold text-white transition-all disabled:opacity-50"
                                        >
                                            {loading ? (
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            ) : (
                                                <>
                                                    <span>Load More Articles</span>
                                                    <ArrowRight className="w-4 h-4" />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}

                                {/* No Results */}
                                {!loading && filteredPosts.length === 0 && (
                                    <div className="text-center py-16">
                                        <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                                            <Search className="w-8 h-8 text-slate-500" />
                                        </div>
                                        <h3 className="text-xl font-bold text-white mb-2">No articles found</h3>
                                        <p className="text-slate-400">Try adjusting your search or filter criteria</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </section>

                {/* Newsletter CTA */}
                <section className="px-4 pb-24">
                    <div className="max-w-4xl mx-auto">
                        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 border border-emerald-500/30 p-8 md:p-12">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/20 rounded-full blur-[80px]" />

                            <div className="relative z-10 text-center">
                                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                                    Get Career Tips in Your Inbox
                                </h3>
                                <p className="text-slate-400 mb-6 max-w-xl mx-auto">
                                    Join 10,000+ professionals getting weekly career insights and job search tips.
                                </p>
                                <Link
                                    to="/build-roadmap"
                                    className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-bold rounded-xl flex items-center shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 transition-all hover:-translate-y-1"
                                >
                                    <span>Get Free Career Roadmap</span>
                                    <ArrowRight className="w-5 h-5" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default BlogListPage;
